import { ANNUAL_PLAN, MONTHLY_PLAN } from "../shared/constants";
import { isValidEmail } from "../shared/utils";
import type { SparksCancellationWinbackPlans } from "./SparksCancellationWinbackPaywall";
import type { SparksCancellationWinbackResolution } from "./SparksCancellationWinbackResolver";

export const FREE_MONTH_MONTHLY_STRIPE_HREF = "https://buy.stripe.com/fZubJ0ceLaYg7OH2ufew80G";
export const FREE_MONTH_ANNUAL_STRIPE_HREF = "https://buy.stripe.com/dRmaEW1A77M4d91ecXew80H";

export const SPARKS_FREE_MONTH_PLANS: SparksCancellationWinbackPlans = {
  annual: {
    href: FREE_MONTH_ANNUAL_STRIPE_HREF,
    price: "$129.99",
    compactPrice: "$10.83/MO",
    cadence: "year",
    previousPrice: "$239.88/yr",
    desktopSubtext: "$10.83/mo",
    badge: "SAVE 45%",
  },
  monthly: {
    href: FREE_MONTH_MONTHLY_STRIPE_HREF,
    price: "$19.99",
    compactPrice: "$19.99/MO",
    cadence: "month",
  },
};

export const SPARKS_STANDARD_FREE_WEEK_PLANS: SparksCancellationWinbackPlans = {
  annual: {
    href: ANNUAL_PLAN.href,
    price: ANNUAL_PLAN.price,
    compactPrice: ANNUAL_PLAN.perMonthCompact ?? "",
    cadence: ANNUAL_PLAN.unitLabel,
    previousPrice: ANNUAL_PLAN.strikethrough?.replace(/ /g, ""),
    desktopSubtext: ANNUAL_PLAN.subtext?.replace(/ /g, ""),
    badge: ANNUAL_PLAN.badge,
  },
  monthly: {
    href: MONTHLY_PLAN.href,
    price: MONTHLY_PLAN.price,
    compactPrice: `${MONTHLY_PLAN.price}/MO`,
    cadence: MONTHLY_PLAN.unitLabel,
  },
};

export const getSparksCancellationWinbackPlans = (
  resolution: SparksCancellationWinbackResolution,
): SparksCancellationWinbackPlans =>
  resolution === "eligible" ? SPARKS_FREE_MONTH_PLANS : SPARKS_STANDARD_FREE_WEEK_PLANS;

type CheckoutHrefOptions = {
  href: string;
  email?: string;
  winbackId?: string;
};

export const buildSparksCancellationWinbackCheckoutHref = ({ href, email = "", winbackId }: CheckoutHrefOptions) => {
  if (href.startsWith("#")) return href;

  const url = new URL(href);
  if (isValidEmail(email)) {
    url.searchParams.set("prefilled_email", email);
  }

  const normalizedWinbackId = winbackId?.trim();
  if (normalizedWinbackId) {
    url.searchParams.set("client_reference_id", normalizedWinbackId);
  }

  return url.toString();
};

export const getSparksCancellationWinbackCheckoutPlans = ({
  resolution,
  winbackId,
  email = "",
}: {
  resolution: SparksCancellationWinbackResolution;
  winbackId: string;
  email?: string;
}): SparksCancellationWinbackPlans => {
  const plans = getSparksCancellationWinbackPlans(resolution);
  const checkoutWinbackId = resolution === "eligible" ? winbackId : undefined;

  return {
    annual: {
      ...plans.annual,
      href: buildSparksCancellationWinbackCheckoutHref({
        href: plans.annual.href,
        email,
        winbackId: checkoutWinbackId,
      }),
    },
    monthly: {
      ...plans.monthly,
      href: buildSparksCancellationWinbackCheckoutHref({
        href: plans.monthly.href,
        email,
        winbackId: checkoutWinbackId,
      }),
    },
  };
};
